import { Popup } from 'components/Popup'
import { Button, BUTTON_TYPES } from 'components/Button'
import { Typography } from 'components/Typography'
import { Banner } from 'components/Banner/Banner'
import { useFinancialsPopup } from 'features/Financials/Providers/FinancialsPopupProvider'
import { FINANCIAL_TABS } from 'features/Financials/constants'
import useRouter from 'hooks/useRouter'
import { routes } from 'router'
import {
  DISPUTE_PROCESS_CANT_BE_DONE_CONFIG,
  DISPUTE_PROCESS_CANT_BE_DONE_TYPES
} from './constants'
import './styles.scss'
import { useCallback, useMemo } from 'react'
import { formatAmount } from 'helper/common'
import { MarketplaceARRebateQuarter } from '../types'

export default function DisputeProcessCantBeDonePopup() {
  const {
    disputeProcessCantBeDonePopup: { state, actions },
    invoicesDetailsPopup
  } = useFinancialsPopup()
  const { navigate } = useRouter()
  const { correlationCheck } = state.payload

  const type = useMemo(() => {
    if (
      correlationCheck?.not_paid_rebate_quarters?.length &&
      correlationCheck?.paid_rebate_quarters?.length
    ) {
      return DISPUTE_PROCESS_CANT_BE_DONE_TYPES.SOME_DISPUTES_UNAVAILABLE
    }
    if (correlationCheck?.paid_rebate_quarters?.length) {
      return DISPUTE_PROCESS_CANT_BE_DONE_TYPES.DISPUTE_UNAVAILABLE
    }
    return DISPUTE_PROCESS_CANT_BE_DONE_TYPES.CENTRAL_DISPUTE_LOCATION
  }, [
    correlationCheck?.not_paid_rebate_quarters,
    correlationCheck?.paid_rebate_quarters
  ])

  const config = DISPUTE_PROCESS_CANT_BE_DONE_CONFIG[type]

  const onCancel = () => actions.close()

  const onPrimaryClick = () => {
    actions.close()
    if (type !== DISPUTE_PROCESS_CANT_BE_DONE_TYPES.DISPUTE_UNAVAILABLE) {
      invoicesDetailsPopup.actions.close()
    }
    if (config?.footer.goToRebates) {
      navigate(`${routes.financials}/${FINANCIAL_TABS[0].key}`)
    }
  }

  const calculations = useMemo(() => {
    if (
      !correlationCheck ||
      type === DISPUTE_PROCESS_CANT_BE_DONE_TYPES.DISPUTE_UNAVAILABLE
    )
      return []
    return correlationCheck?.not_paid_rebate_quarters
  }, [correlationCheck, type])

  const onOpenRebateInvoices = useCallback(
    (quarter: MarketplaceARRebateQuarter) => {
      actions.close()

      invoicesDetailsPopup.actions.close()
      navigate(
        `${routes.financials}/${FINANCIAL_TABS[0].key}?invoiceId=${
          quarter.uuid
        }&period_ending=${encodeURIComponent(quarter.period_ending)}`
      )
    },
    [actions, invoicesDetailsPopup.actions, navigate]
  )

  return (
    <Popup
      {...state}
      title={config?.title}
      width={442}
      className="dispute-process-cant-be-done"
      footer={
        config ? (
          <>
            {config.footer.cancel && (
              <Button onClick={onCancel} type={BUTTON_TYPES.DEFAULT} upperCase>
                {config.footer.cancel}
              </Button>
            )}
            <Button
              type={BUTTON_TYPES.PRIMARY}
              onClick={onPrimaryClick}
              upperCase
            >
              {config.footer.primary}
            </Button>
          </>
        ) : null
      }
    >
      {config && (
        <div className="dispute-process-cant-be-done__content column gap-16">
          {config.paragraphs.map((paragraph) => (
            <Typography.Body1 key={paragraph}>{paragraph}</Typography.Body1>
          ))}
          {!!calculations.length && (
            <ul className="dispute-process-cant-be-done__calculations">
              {calculations.map((item, index) => (
                <li
                  key={`${item}-${index}`}
                  onClick={() => onOpenRebateInvoices(item)}
                >
                  <Typography.Body1 className="main-color-text">
                    {item.hospital_name} · {formatAmount(item.total)}
                  </Typography.Body1>
                </li>
              ))}
            </ul>
          )}
          {type !==
            DISPUTE_PROCESS_CANT_BE_DONE_TYPES.CENTRAL_DISPUTE_LOCATION && (
            <Banner.Info
              text={
                <>
                  If you need to raise an issue with these invoices, please
                  contact support at{' '}
                  <a
                    href="mailto:info@ominnovations.health"
                    className="small-text"
                  >
                    info@ominnovations.health
                  </a>
                </>
              }
            />
          )}
        </div>
      )}
    </Popup>
  )
}
